import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Web3 from 'web3';
import { Clock, AlertTriangle, Check, X, RefreshCw } from 'lucide-react';
import EscrowContractABI from './EscrowContractABI.json';
import './HistoryPage.css';
import Popup from './Popup';

const escrowContractAddress = '0xB7d3164045A77F18165A748a15aa02A85aF70A27';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function HistoryPage() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [userAddress, setUserAddress] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [web3, setWeb3] = useState(null);
  const [escrowContract, setEscrowContract] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');

  const fetchTransactions = async () => {
    if (!web3 || !userAddress || !escrowContract) return;

    try {
      console.log('Fetching transactions for address:', userAddress);

      const response = await axios.get(`${API_URL}/api/transactions?address=${userAddress}`);

      console.log('API Response:', JSON.stringify(response.data, null, 2));

      const transactionsWithStatus = await Promise.all(
        response.data.map(async (transaction) => {
          const transactionId = transaction.transactionId;
          if (!transactionId) {
            console.error('Transaction ID is missing for transaction:', transaction);
            return transaction;
          }

          try {
            const blockchainTransaction = await escrowContract.methods.transactions(transactionId).call();
            const now = Math.floor(Date.now() / 1000);
            const timeLimit = await escrowContract.methods.defaultTimeLimit().call();
            const isExpired = now > parseInt(blockchainTransaction.createdAt) + parseInt(timeLimit);

            return {
              ...transaction,
              isExpired,
              blockchainStatus: blockchainTransaction.isComplete ? 'Completed' :
                blockchainTransaction.isDisputed ? 'Disputed' :
                  blockchainTransaction.isWithdrawn ? 'Withdrawn' : 'Pending'
            };
          } catch (error) {
            console.error('Error fetching blockchain data for transaction:', transactionId, error);
            return transaction;
          }
        })
      );

      console.log('Processed transactions:', transactionsWithStatus);

      const filteredTransactions = transactionsWithStatus.reduce((acc, current) => {
        const existingTransaction = acc.find(item => item.listingId === current.listingId);
        if (!existingTransaction) {
          return [...acc, current];
        } else {
          if (current.blockchainStatus === 'Disputed' || (current.blockchainStatus !== 'Withdrawn' && new Date(current.createdAt) > new Date(existingTransaction.createdAt))) {
            return acc.map(item => item.listingId === current.listingId ? current : item);
          }
          return acc;
        }
      }, []);

      setTransactions(filteredTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to fetch transactions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeWeb3 = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          await window.ethereum.request({ method: 'eth_requestAccounts' });
          const web3Instance = new Web3(window.ethereum);
          setWeb3(web3Instance);

          const contractInstance = new web3Instance.eth.Contract(EscrowContractABI, escrowContractAddress);
          setEscrowContract(contractInstance);

          const accounts = await web3Instance.eth.getAccounts();
          setUserAddress(accounts[0]);

          return web3Instance;
        } catch (error) {
          console.error('User denied account access', error);
          setError('Please connect to MetaMask to use this feature.');
          setIsLoading(false);
        }
      } else {
        console.log('Non-Ethereum browser detected. You should consider trying MetaMask!');
        setError('Please install MetaMask to use this feature.');
        setIsLoading(false);
      }
    };

    initializeWeb3();
  }, []);

  useEffect(() => {
    fetchTransactions();

    const intervalId = setInterval(fetchTransactions, 30000);

    return () => clearInterval(intervalId);
  }, [web3, userAddress, escrowContract]);

  const updateTransactionStatus = async (transaction, newStatus) => {
    try {
      if (newStatus === 'Withdrawn') {
        await handleWithdraw(transaction);
      } else if (newStatus === 'Disputed') {
        await handleDispute(transaction);
      }

      const updatedTransaction = { ...transaction, status: newStatus, blockchainStatus: newStatus };
      setTransactions(prevTransactions =>
        prevTransactions.map(tx =>
          tx._id === transaction._id ? updatedTransaction : tx
        )
      );

      await axios.post(`${API_URL}/api/transactions/${transaction._id}/update-status`, {
        status: newStatus
      });
      console.log('Backend updated successfully');
    } catch (error) {
      console.error('Failed to update transaction status:', error);
    }
  };

  const showPopupMessage = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  };

  const handleWithdraw = async (transaction) => {
    if (!escrowContract || !userAddress) {
      console.error('Escrow contract or user address not available');
      return;
    }
    console.log('Attempting to withdraw transaction:', transaction);
    try {
      if (!transaction.transactionId) {
        throw new Error('Transaction ID is missing');
      }

      console.log('Calling withdrawTransaction with ID:', transaction.transactionId);
      const withdrawResult = await escrowContract.methods.withdrawTransaction(transaction.transactionId).send({ from: userAddress });
      console.log('Withdraw transaction result:', withdrawResult);

      const response = await axios.post(`${API_URL}/api/transactions/${transaction.transactionId}/update-status`, {
        status: 'withdrawn'
      });

      console.log('Backend update response:', response.data);

      setTransactions(prevTransactions =>
        prevTransactions.map(tx =>
          tx.transactionId === transaction.transactionId
            ? { ...tx, blockchainStatus: 'Withdrawn', status: 'withdrawn' }
            : tx
        )
      );

      showPopupMessage('Transaction withdrawn successfully! The listing is now Available.');

      await fetchTransactions();

      return withdrawResult;
    } catch (error) {
      console.error('Error withdrawing transaction:', error);
      showPopupMessage(`Failed to withdraw transaction: ${error.message}`);
      throw error;
    }
  };

  const handleDispute = async (transaction) => {
    if (!escrowContract || !userAddress) {
      console.error('Escrow contract or user address not available');
      return;
    }

    console.log('Attempting to raise dispute for transaction:', transaction);
    try {
      if (!transaction.transactionId) {
        throw new Error('Transaction ID is missing');
      }

      console.log('Calling disputeTransaction with ID:', transaction.transactionId);
      const disputeResult = await escrowContract.methods.disputeTransaction(transaction.transactionId).send({ from: userAddress });
      console.log('Dispute transaction result:', disputeResult);

      showPopupMessage('Dispute raised successfully!');
      return disputeResult;
    } catch (error) {
      console.error('Error raising dispute:', error);
      throw error;
    }
  };

  const handleComplete = async (transaction) => {
    if (!escrowContract || !userAddress) {
      console.error('Escrow contract or user address not available');
      return;
    }

    console.log('Attempting to complete transaction:', transaction);
    try {
      if (!transaction.transactionId) {
        throw new Error('Transaction ID is missing');
      }

      console.log('Calling completeTransaction with ID:', transaction.transactionId);
      const completeResult = await escrowContract.methods.completeTransaction(transaction.transactionId).send({ from: userAddress });
      console.log('Complete transaction result:', completeResult);

      const response = await axios.post(`${API_URL}/api/transactions/${transaction.transactionId}/update-status`, {
        status: 'completed'
      });

      console.log('API response:', response.data);

      setTransactions(prevTransactions =>
        prevTransactions.map(tx =>
          tx.transactionId === transaction.transactionId
            ? { ...tx, blockchainStatus: 'Completed', status: 'completed' }
            : tx
        )
      );

      showPopupMessage('Transaction completed successfully!');
    } catch (error) {
      console.error('Error completing transaction:', error);
      showPopupMessage(`Failed to complete transaction: ${error.message}`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Disputed':
        return <AlertTriangle className="status-icon disputed" />;
      case 'Withdrawn':
        return <X className="status-icon withdrawn" />;
      case 'Pending':
        return <Clock className="status-icon pending" />;
      case 'Completed':
        return <Check className="status-icon completed" />;
      default:
        return <RefreshCw className="status-icon" />;
    }
  };

  if (isLoading) {
    return <div className="history-page loading">Loading transactions...</div>;
  }

  if (error) {
    return <div className="history-page error">Error: {error}</div>;
  }

  return (
    <div className="history-page">
      <div className="history-container">
        <h1>Your Transaction History</h1>
        {transactions.length === 0 ? (
          <p className="no-transactions">You have no transactions yet.</p>
        ) : (
          <div className="transactions-grid">
            {transactions.map((transaction) => (
              <div key={transaction._id} className={`transaction-card ${transaction.role}`}>
                <div className="transaction-header">
                  <h2>{transaction.listingTitle}</h2>
                  {getStatusIcon(transaction.blockchainStatus)}
                </div>
                <div className="transaction-details">
                  <p><strong>Price:</strong> {transaction.price} USDT</p>
                  <p><strong>Status:</strong> {transaction.blockchainStatus}</p>
                  <p><strong>Your Role:</strong> {transaction.role}</p>
                  <p>
                    <strong>{transaction.role === 'buyer' ? 'Seller' : 'Buyer'}:</strong>{' '}
                    <span className="address">
                      {transaction.role === 'buyer' ? transaction.seller : transaction.buyer}
                    </span>
                  </p>
                </div>
                <p className="status-message">{getStatusIcon(transaction.blockchainStatus)}</p>
                <div className="transaction-actions">
                  {transaction.blockchainStatus === 'Pending' && transaction.role === 'buyer' && !transaction.isExpired && (
                    <button onClick={() => handleComplete(transaction)} className="action-button complete">
                      Complete Transaction
                    </button>
                  )}
                  {transaction.blockchainStatus === 'Pending' && !transaction.isExpired && (
                    <button onClick={() => updateTransactionStatus(transaction, 'Disputed')} className="action-button dispute">
                      Raise Dispute
                    </button>
                  )}
                  {transaction.blockchainStatus === 'Pending' && transaction.isExpired && transaction.role === 'buyer' && (
                    <button onClick={() => updateTransactionStatus(transaction, 'Withdrawn')} className="action-button withdraw">
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {showPopup && (
          <Popup
            message={popupMessage}
            onClose={() => setShowPopup(false)}
          />
        )}
      </div>
    </div>
  );
}

export default HistoryPage;